import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Typography from '@mui/material/Typography';

import FolderIcon from '@mui/icons-material/Folder';
// import DownloadIcon from '@mui/icons-material/Download';

import ProjectForm from '../forms/ProjectForm';
import { useProjectsSelectorContext } from '../contexts/ProjectsDataContext';

// -----------------------------------------------------------------------------
// Downloading link-related
//
// The S3 artifacts directory is the source of truth: etl-rs lists what is
// actually there. A file appearing in the listing is a file that can be
// downloaded — no separate registry of expected names, no staleness guessing.
const ARTIFACTS_ENDPOINT = process.env.REACT_APP_ARTIFACTS_ENDPOINT;

const listUrl = (projectId) =>
  ARTIFACTS_ENDPOINT.replace('{projectId}', projectId);

const downloadUrl = (projectId, filename) =>
  `${listUrl(projectId)}/${encodeURIComponent(filename)}`;

// Blurbs for the artifacts we know about. Anything else still lists and
// downloads; it just shows without a description.
const DESCRIPTIONS = {
  'matrix.csv':
    'Hosts the requested data and computed derived fields for a selected subject universe',
  'matrix.feather':
    'Hosts the requested data and computed derived fields in the feather file format',
  'matrix.parquet':
    'Hosts the requested data and computed derived fields in the parquet file format',
};

const formatSize = (bytes) => {
  if (typeof bytes !== 'number' || bytes < 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${
    units[unit]
  }`;
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const describe = ({ name, size, lastModified }) =>
  [DESCRIPTIONS[name], [formatSize(size), formatWhen(lastModified)]
    .filter(Boolean)
    .join(' · ')]
    .filter(Boolean)
    .join(' — ');
// -----------------------------------------------------------------------------

const NewProjectPage = () => {
  return (
    <Box sx={{ m: '30px' }}>
      <Typography variant='h5' component='h6'>
        New Project
      </Typography>
      Create new project form
      <Divider sx={{ m: 2, mt: 4, mb: 4 }} />
      <ProjectForm />
    </Box>
  );
};

/// Fetch the project's artifacts from etl-rs.
const useArtifacts = (projectId) => {
  const [state, setState] = useState({ artifacts: [], loading: true, error: null });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const resp = await fetch(listUrl(projectId), {
          credentials: 'include',
        });
        if (!resp.ok) {
          throw new Error(`Failed to list artifacts (${resp.status})`);
        }
        const body = await resp.json();
        if (!cancelled) {
          setState({
            artifacts: body.results ?? [],
            loading: false,
            error: null,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setState({ artifacts: [], loading: false, error: e.message });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return state;
};

const ProjectMeta = () => {
  const { projectId } = useParams();
  const { select } = useProjectsSelectorContext();
  const projectMeta = select(projectId);

  const { artifacts, loading, error } = useArtifacts(projectId);

  return (
    <div className='project-page root'>
      <h5>Project Meta</h5>
      <Divider sx={{ m: 2, mt: 4, mb: 4 }} />
      <ProjectForm data={projectMeta} />
      <Divider sx={{ m: 2, mt: 4, mb: 8 }} />
      <h5>Artifacts</h5>
      <div>Downloadable files</div>
      {loading && <div className='artifacts status'>Loading…</div>}
      {error && <div className='artifacts status error'>{error}</div>}
      {!loading && !error && artifacts.length === 0 && (
        <div className='artifacts status empty'>
          No artifacts yet — run a matrix request to generate them.
        </div>
      )}
      <List className='project-artifacts root'>
        {artifacts.map((artifact) => (
          <div key={artifact.name} className='project-artifact root'>
            <a
              className='link'
              href={downloadUrl(projectId, artifact.name)}
              download>
              <ListItem className='project-artifact item'>
                <ListItemAvatar className='artifact avatar'>
                  <Avatar>
                    <FolderIcon className='icon' />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  className='artifact text'
                  primary={artifact.name}
                  secondary={describe(artifact)}
                />
              </ListItem>
            </a>
          </div>
        ))}
      </List>
    </div>
  );
};

export { ProjectMeta, NewProjectPage };
export default NewProjectPage;
